import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import apiClient from '@/utils/api';
import ImageUpload from '@/components/ImageUpload';

export default function AdminFooterSettings() {
  const [settings, setSettings] = useState({
    logo_url: '',
    tagline: '',
    quick_links: [],
    contact_email: '',
    contact_email_link: '',
    contact_phone: '',
    contact_phone_link: '',
    contact_address: '',
    contact_address_link: '',
    social_links: [],
    copyright_text: '',
    padding_top: '90px',
    padding_bottom: '64px',
    padding_left: '16px',
    padding_right: '16px'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await apiClient.get('/admin/footer');
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching footer settings:', error);
      toast.error('Failed to load footer settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiClient.put('/admin/footer', settings);
      toast.success('Footer settings saved successfully!');
    } catch (error) {
      toast.error('Failed to save footer settings');
      console.error('Error saving settings:', error);
    } finally {
      setSaving(false);
    }
  };

  const addQuickLink = () => {
    setSettings({
      ...settings,
      quick_links: [...settings.quick_links, { label: '', url: '' }]
    });
  };

  const updateQuickLink = (index, field, value) => {
    const updatedLinks = [...settings.quick_links];
    updatedLinks[index][field] = value;
    setSettings({ ...settings, quick_links: updatedLinks });
  };

  const removeQuickLink = (index) => {
    const updatedLinks = settings.quick_links.filter((_, i) => i !== index);
    setSettings({ ...settings, quick_links: updatedLinks });
  };

  const addSocialLink = () => {
    setSettings({
      ...settings,
      social_links: [...settings.social_links, { platform: '', url: '' }]
    });
  };

  const updateSocialLink = (index, field, value) => {
    const updatedLinks = [...settings.social_links];
    updatedLinks[index][field] = value;
    setSettings({ ...settings, social_links: updatedLinks });
  };

  const removeSocialLink = (index) => {
    const updatedLinks = settings.social_links.filter((_, i) => i !== index);
    setSettings({ ...settings, social_links: updatedLinks });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <Link to="/admin" className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-4">
            <ArrowLeft size={20} className="mr-2" />
            Back to Dashboard
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Footer Settings</h1>
            <p className="text-gray-600 mt-1">Manage your website footer content</p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-md p-6 space-y-6">
          
          <ImageUpload
            label="Footer Logo"
            value={settings.logo_url}
            onChange={(url) => setSettings({ ...settings, logo_url: url })}
            required={false}
            preview={true}
          />

          <div>
            <Label className="text-lg font-semibold mb-3 block">Footer Padding</Label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <Label className="text-sm">Top</Label>
                <Input
                  value={settings.padding_top}
                  onChange={(e) => setSettings({ ...settings, padding_top: e.target.value })}
                  placeholder="90px"
                />
              </div>
              <div>
                <Label className="text-sm">Bottom</Label>
                <Input
                  value={settings.padding_bottom}
                  onChange={(e) => setSettings({ ...settings, padding_bottom: e.target.value })}
                  placeholder="64px"
                />
              </div>
              <div>
                <Label className="text-sm">Left</Label>
                <Input
                  value={settings.padding_left}
                  onChange={(e) => setSettings({ ...settings, padding_left: e.target.value })}
                  placeholder="16px"
                />
              </div>
              <div>
                <Label className="text-sm">Right</Label>
                <Input
                  value={settings.padding_right}
                  onChange={(e) => setSettings({ ...settings, padding_right: e.target.value })}
                  placeholder="16px"
                />
              </div>
            </div>
          </div>

          <div>
            <Label>Tagline</Label>
            <Input
              value={settings.tagline}
              onChange={(e) => setSettings({ ...settings, tagline: e.target.value })}
              placeholder="Where ambitions take shape"
            />
          </div>

          <div>
            <div className="flex justify-between items-center mb-4">
              <Label className="text-lg font-semibold">Quick Links</Label>
              <Button onClick={addQuickLink} size="sm" variant="outline">
                <Plus size={16} className="mr-1" />
                Add Link
              </Button>
            </div>
            
            <div className="space-y-3">
              {settings.quick_links.map((link, index) => (
                <div key={index} className="flex gap-3 items-end">
                  <div className="flex-1">
                    <Label className="text-sm">Label</Label>
                    <Input
                      value={link.label}
                      onChange={(e) => updateQuickLink(index, 'label', e.target.value)}
                      placeholder="About Us"
                    />
                  </div>
                  <div className="flex-1">
                    <Label className="text-sm">URL</Label>
                    <Input
                      value={link.url}
                      onChange={(e) => updateQuickLink(index, 'url', e.target.value)}
                      placeholder="#about"
                    />
                  </div>
                  <Button
                    onClick={() => removeQuickLink(index)}
                    size="sm"
                    variant="destructive"
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-lg font-semibold mb-3 block">Contact Information</Label>
            <p className="text-sm text-gray-500 mb-4">Make your contact details clickable by adding link URLs</p>
            
            <div className="space-y-4">
              {/* Email */}
              <div className="p-4 border rounded-lg bg-gray-50">
                <Label className="font-semibold mb-3 block">Email</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm text-gray-600">Display Text</Label>
                    <Input
                      value={settings.contact_email}
                      onChange={(e) => setSettings({ ...settings, contact_email: e.target.value })}
                      placeholder="info@thecircuit.work"
                    />
                  </div>
                  <div>
                    <Label className="text-sm text-gray-600">Link URL (mailto:)</Label>
                    <Input
                      value={settings.contact_email_link}
                      onChange={(e) => setSettings({ ...settings, contact_email_link: e.target.value })}
                      placeholder="mailto:info@thecircuit.work"
                    />
                  </div>
                </div>
              </div>

              {/* Phone */}
              <div className="p-4 border rounded-lg bg-gray-50">
                <Label className="font-semibold mb-3 block">Phone</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm text-gray-600">Display Text</Label>
                    <Input
                      value={settings.contact_phone}
                      onChange={(e) => setSettings({ ...settings, contact_phone: e.target.value })}
                      placeholder="+91 98765 43210"
                    />
                  </div>
                  <div>
                    <Label className="text-sm text-gray-600">Link URL (tel:)</Label>
                    <Input
                      value={settings.contact_phone_link}
                      onChange={(e) => setSettings({ ...settings, contact_phone_link: e.target.value })}
                      placeholder="tel:+919876543210"
                    />
                  </div>
                </div>
              </div>

              {/* Address */}
              <div className="p-4 border rounded-lg bg-gray-50">
                <Label className="font-semibold mb-3 block">Address</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm text-gray-600">Display Text</Label>
                    <Textarea
                      value={settings.contact_address}
                      onChange={(e) => setSettings({ ...settings, contact_address: e.target.value })}
                      placeholder="Bangalore, India"
                      rows={2}
                    />
                  </div>
                  <div>
                    <Label className="text-sm text-gray-600">Link URL (Google Maps)</Label>
                    <Textarea
                      value={settings.contact_address_link}
                      onChange={(e) => setSettings({ ...settings, contact_address_link: e.target.value })}
                      placeholder="https://maps.google.com/?q=Bangalore,India"
                      rows={2}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div>
            <div className="flex justify-between items-center mb-4">
              <Label className="text-lg font-semibold">Social Media Links</Label>
              <Button onClick={addSocialLink} size="sm" variant="outline">
                <Plus size={16} className="mr-1" />
                Add Link
              </Button>
            </div>
            
            <div className="space-y-3">
              {settings.social_links.map((link, index) => (
                <div key={index} className="flex gap-3 items-end">
                  <div className="flex-1">
                    <Label className="text-sm">Platform</Label>
                    <Input
                      value={link.platform}
                      onChange={(e) => updateSocialLink(index, 'platform', e.target.value)}
                      placeholder="facebook"
                    />
                  </div>
                  <div className="flex-1">
                    <Label className="text-sm">URL</Label>
                    <Input
                      value={link.url}
                      onChange={(e) => updateSocialLink(index, 'url', e.target.value)}
                      placeholder="https://facebook.com/..."
                    />
                  </div>
                  <Button
                    onClick={() => removeSocialLink(index)}
                    size="sm"
                    variant="destructive"
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <div>
            <Label>Copyright Text</Label>
            <Input
              value={settings.copyright_text}
              onChange={(e) => setSettings({ ...settings, copyright_text: e.target.value })}
              placeholder="© 2024 The Circuit. All rights reserved."
            />
          </div>

          <div className="flex justify-end pt-4 border-t">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Save size={18} className="mr-2" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
